import React from 'react';
import { useLingui } from '@lingui/react/macro';

import { Flex, Heading } from '@rover/kibble/core';
import type { Attribute } from '@rover/rsdk/src/apiClient/latest/model/attribute';

import { AttributeBlock } from './RevampHomeCharacteristics';

const PETS_AND_PEOPLE_KEYS = new Set([
  'dogs',
  'cats',
  'no-dogs',
  'owns-caged-pets',
  'children-0-5',
  'children-6-12',
  'no-children',
]);

type AttributeListProps = {
  title: string;
  attributes: Attribute[];
  testId: string;
};

const AttributeList = ({ title, attributes, testId }: AttributeListProps): JSX.Element | null => {
  if (attributes.length === 0) return null;

  return (
    <Flex flex="1" minWidth="0" flexDirection="column" alignItems="flex-start" data-testid={testId}>
      <Heading size="300">{title}</Heading>
      {attributes.map((attribute) => (
        <AttributeBlock attribute={attribute} key={attribute.key} minWidth="0" />
      ))}
    </Flex>
  );
};

type Props = {
  attributes: Attribute[];
};

const RevampHomeFeaturesAndHousehold = ({ attributes }: Props): JSX.Element | null => {
  const { t } = useLingui();

  if (attributes.length === 0) return null;

  const petsAndPeople = attributes.filter((attribute) => PETS_AND_PEOPLE_KEYS.has(attribute.key));
  const homeFeatures = attributes.filter((attribute) => !PETS_AND_PEOPLE_KEYS.has(attribute.key));

  return (
    <Flex
      flexDirection={['column', 'row']}
      width="100%"
      py="6x"
      sx={{ columnGap: '6x', rowGap: '10x' }}
    >
      <AttributeList
        title={t`Home features`}
        attributes={homeFeatures}
        testId="revamp-home-features"
      />
      <AttributeList
        title={t`Pets & people`}
        attributes={petsAndPeople}
        testId="revamp-pets-and-people"
      />
    </Flex>
  );
};

export default RevampHomeFeaturesAndHousehold;
